import { StatusApi } from "@/constants/default";
import { useUpdateMeMutation } from "@/services/auth.service";
import { AppDispatch, RootState } from "@/store";
import { addAuth } from "@/store/features/auth.slice";
import { AuthUpdate } from "@/types/auth.type";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

export default function Profile() {
    const {t} = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const { auth } = useSelector((state: RootState) => state.auth)
    const [updateMe, { data, isSuccess}] = useUpdateMeMutation()
    const {register, handleSubmit, formState: {errors}, watch, setValue} = useForm<AuthUpdate>()
    const dispatch = useDispatch<AppDispatch>()

    const onSubmit = handleSubmit((data) => {
        updateMe(data)
    })

    useEffect(() => {
        if(auth) {
            setValue("name", auth.name)
            setValue("email", auth.email)
        }
    }, [auth])

    useEffect(() => {
        if(isSuccess) {
            dispatch(addAuth(data?.data))
            navigate(location, {state: ""})
        }
    }, [isSuccess])

    return (
        <div x-show="modalOpen" x-transition="" className="fixed top-0 left-0 z-999 flex h-full min-h-screen w-full items-center justify-center bg-black/90 px-4 py-5">
            <div className="w-full max-w-screen-sm rounded-lg bg-white py-5 px-8 dark:bg-boxdark">
                <h3 className="text-xl dark:text-white text-center font-medium my-5">
                    {t("profile.update_profile")}
                </h3>
                <form className="mx-5" onSubmit={onSubmit}>
                    <div className="mb-4">
                        <label className="mb-2.5 block text-black dark:text-white">
                            {t("user.name")}
                        </label>
                        <input
                            type="text"
                            placeholder={t("placeholder.name")}
                            className="w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:text-white dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                            {...register("name", {required: t("validation.required")})}
                        />
                        { errors.name && <p className="mt-1 text-rose-600">{errors.name.message}</p>}
                    </div>
                    <div className="mb-4">
                        <label className="mb-2.5 block text-black dark:text-white">
                            {t("user.email")}
                        </label>
                        <input
                            type="email"
                            placeholder={t("placeholder.email")}
                            disabled={true}
                            className="w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:text-white dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                            {...register("email")}
                        />
                        { errors.email && <p className="mt-1 text-rose-600">{errors.email.message}</p>}
                    </div>
                    <div className="mt-7 mb-5 border dark:border-form-strokedark"></div>
                    <div className="mb-4">
                        <label className="mb-2.5 block text-black dark:text-white">
                            {t("user.old_password")}
                        </label>
                        <input
                            type="password"
                            placeholder={t("placeholder.old_password")}
                            className="w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                            {...register("old_password", {required: watch('password') ? t("validation.required") : false})}
                        />
                        { errors.old_password && <p className="mt-1 text-rose-600">{errors.old_password.message}</p>}
                    </div>
                    <div className="mb-4">
                        <label className="mb-2.5 block text-black dark:text-white">
                            {t("user.password")}
                        </label>
                        <input
                            type="password"
                            placeholder={t("placeholder.password")}
                            className="w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                            {...register("password", {required: watch('old_password') ? t("validation.required") : false})}
                        />
                        { errors.password && <p className="mt-1 text-rose-600">{errors.password.message}</p>}
                    </div>
                    <div className="mb-4">
                        <label className="mb-2.5 block text-black dark:text-white">
                            {t("user.retype_password")}
                        </label>
                        <input
                            type="password"
                            placeholder={t("placeholder.password")}
                            className="w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                            {...register("retype_password", {
                                required: watch('old_password') ? t("validation.required") : false,
                                validate: (val: string) => {
                                    if (watch('password') != val) {
                                      return t("validation.password_not_match");
                                    }
                                },
                            })}
                        />
                        { errors.retype_password && <p className="mt-1 text-rose-600">{errors.retype_password.message}</p>}
                    </div>
                    <div className="flex justify-end">
                        <div className="px-6">
                            <button type="button" onClick={() => navigate(location)} className="block w-full rounded border border-stroke bg-gray p-1.5 px-3 text-center font-medium text-black transition dark:text-white">
                                {t("modal.cancel")}
                            </button>
                        </div>
                        <div>
                            <button type="submit" className="flex w-full justify-center rounded bg-primary p-1.5 px-3 text-white">
                                {t("modal.submit")}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}